import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import { Popconfirm, Table, Tooltip } from "antd";
import { Modal } from "@/components/ui/modal";
import { Button as AntdButton } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faPenToSquare,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useLinkFerence } from "../../../../../hooks/useLinkReference";
import { toast } from "react-toastify";
import Markdown from "react-markdown";
import { markdownStyles } from "../../../../../components/Markdown/markdownStyle";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import TableToolbar from "../reference-data/component/tableToolbar";
import { TablePagination } from "../../../../../components/pagination/TablePagination";
import { convertToVNTime } from "../../../../../utils/Date";
import { FormattedMessage, useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";

const ManageLink = ({ tableProps = {}, className = "" }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createLink, setCreateLink] = useState("");
  const [createDetail, setCreateDetail] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 5,
    name: "",
  });

  const [searchDraft, setSearchDraft] = useState({
    page: 1,
    limit: 5,
    name: "",
  });

  const [localLinkReferenceData, setLocalLinkReferenceData] = useState({
    result: [],
    total: 0,
  });

  const {
    linkreference,
    linkreferenceLoading,
    createLinkReferenceAsync,
    updateLinkReferenceAsync,
    importLinkReferenceAsync,
    deleteLinkReferenceAsync,
  } = useLinkFerence(searchParams);

  useEffect(() => {
    if (linkreference) {
      setLocalLinkReferenceData(linkreference);
    }
  }, [linkreference]);

  // Đồng bộ page state với searchParams.page
  useEffect(() => {
    setPage(searchParams.page);
  }, [searchParams.page]);

  useEffect(() => {
    if (
      !linkreferenceLoading &&
      localLinkReferenceData?.result?.length === 0 &&
      searchParams.page > 1
    ) {
      setSearchParams((prev) => ({
        ...prev,
        page: 1,
      }));
      const invalidateQueries = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [
            "linkreference-data",
            searchParams?.page,
            searchParams?.limit,
            searchParams?.name,
          ],
        });
      }, 2000);
      setPage(searchParams?.page);
      return () => clearTimeout(invalidateQueries);
    }
  }, [
    localLinkReferenceData?.result?.length,
    linkreferenceLoading,
    searchParams.page,
  ]);

  const fileInputRef = useRef();

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams((prev) => ({
      ...prev,
      ...searchDraft,
      page: newPage,
      limit: limit,
    }));
  };

  const handlePageSizeChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
    setSearchParams((prev) => ({
      ...prev,
      ...searchDraft,
      page: 1,
      limit: newLimit,
    }));
  };

  const handleImportClick = () => {
    if (fileInputRef.current) fileInputRef.current.value = null;
    fileInputRef.current?.click();
  };


  // Hàm parse toàn bộ nội dung CSV để xử lý đúng dữ liệu nhiều dòng
  const parseCSVContent = (text) => {
    const records = [];
    let currentRecord = [];
    let currentField = '';
    let inQuotes = false;
    let i = 0;
    
    while (i < text.length) {
      const char = text[i];
      
      if (char === '"') {
        if (inQuotes && text[i + 1] === '"') {
          // Escaped quote (double quotes)
          currentField += '"';
          i += 2; // Skip both quotes
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
          i++;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        currentRecord.push(currentField);
        currentField = '';
        i++;
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        // End of record
        currentRecord.push(currentField);
        if (currentRecord.length > 0) {
          records.push(currentRecord);
        }
        currentRecord = [];
        currentField = '';
        // Skip \r\n combination
        if (char === '\r' && text[i + 1] === '\n') {
          i += 2;
        } else {
          i++;
        }
      } else {
        // Giữ nguyên mọi ký tự khác (bao gồm \n, \r trong quotes)
        currentField += char;
        i++;
      }
    }
    
    // Add the last field and record
    if (currentField !== '' || currentRecord.length > 0) {
      currentRecord.push(currentField);
      if (currentRecord.length > 0) {
        records.push(currentRecord);
      }
    }
    
    return records;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Validate định dạng file CSV
    const isCsv =
      file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");
    if (!isCsv) {
      toast.error(intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.INVALID_FILE" }));
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      
      // Xử lý CSV đúng cách - parse toàn bộ file thành các record
      const records = parseCSVContent(text);
      if (records.length === 0) {
        toast.error(intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.EMPTY_FILE" }));
        return;
      }
      
      // Kiểm tra header
      const headers = records[0];
      if (
        headers[0].trim() !== "name" ||
        headers[1].trim() !== "link" ||
        headers[2].trim() !== "detail"
      ) {
        toast.error(
          intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.INVALID_HEADERS" })
        );
        return;
      }
      
      // Lọc các record hợp lệ (có giá trị ở cả 3 cột)
      const validRecords = [records[0]];
      for (let i = 1; i < records.length; i++) {
        const record = records[i];
        if (
          record[0] &&
          record[1] &&
          record[2] &&
          record[0].trim() !== "" &&
          record[1].trim() !== "" &&
          record[2].trim() !== ""
        ) {
          validRecords.push(record);
        }
      }
      if (validRecords.length === 1) {
        toast.error(intl.formatMessage({ id: "AGENTS.REFERENCE_DATA.INVALID_DATA" }));
        return;
      }
      
      // Tạo file CSV mới từ các record hợp lệ
      const newCsv = validRecords.map(record => 
        record.map(field => `"${field.replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      const newFile = new File([newCsv], file.name, { type: file.type });

      // Gọi API để import (ngầm)
      try {
        const response = await importLinkReferenceAsync(newFile);
        const importedItems = response?.imported.map((row) => {
          const { id, name, link, detail, created_at } = row;
          return {
            id: id,
            name,
            link,
            detail,
            created_at: created_at,
          };
        });

        setLocalLinkReferenceData((prev) => {
          const newTotal = (prev.total || 0) + importedItems.length;
          const currentPageSize = prev.result.length;

          // Nếu page hiện tại đã đầy, chuyển về page cuối để hiển thị dữ liệu mới
          if (currentPageSize >= limit) {
            const lastPage = Math.ceil(newTotal / limit);

            setSearchParams((prevParams) => ({
              ...prevParams,
              page: lastPage,
            }));
            const invalidateQueries = setTimeout(() => {
              queryClient.invalidateQueries({
                queryKey: [
                  "linkreference-data",
                  searchParams?.page,
                  searchParams?.limit,
                  searchParams?.name,
                ],
              });
            }, 2000);
            return () => clearTimeout(invalidateQueries);
          }

          // Nếu page hiện tại còn chỗ, thêm dữ liệu mới vào đầu
          return {
            ...prev,
            result: [...importedItems, ...prev.result],
            total: newTotal,
          };
        });
      } catch (error) {
        console.error("Lỗi khi import:", error);
      }
    };
    reader.readAsText(file);
  };

  const isMessageLong = (text) => {
    if (!text) return false;

    // Check if text has multiple lines or is longer than 100 characters
    const hasMultipleLines = text.includes("\n");
    const isLongText = text.length > 150;

    return hasMultipleLines || isLongText;
  };

  const handleSearchChange = (e, isNowEmpty = false) => {
    const { name, value } = e.target;
    setSearchDraft((prev) => {
      const nextDraft = { ...prev, [name]: value };
      if (isNowEmpty) {
        setPage(1);
        setSearchParams((prevParams) => ({
          ...prevParams,
          ...nextDraft,
          page: 1,
          limit: limit, // Cập nhật limit từ state hiện tại
        }));
      }
      return nextDraft;
    });
  };

  const handleSearch = () => {
    setSearchParams({
      ...searchParams,
      ...searchDraft,
      page: 1, // Reset về trang đầu tiên khi search
      limit: limit, // Cập nhật limit từ state hiện tại
    });
    setPage(1); // Cập nhật state page để UI đồng bộ
  };

  const [editingItem, setEditingItem] = useState(null);

  const handleOpenCreateModal = () => {
    setEditingItem(null);
    setCreateModalOpen(true);
    setCreateName("");
    setCreateLink("");
    setCreateDetail("");
    setValidationErrors({});
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setValidationErrors({});
  };

  const handleSubmitCreate = async () => {
    // Reset validation errors
    setValidationErrors({});

    // Validate required fields
    const errors = {};
    if (!createName.trim()) {
      errors.name = intl.formatMessage({
        id: "AGENTS.HOME.REFERENCE_DATA.ERROR.NAME",
      });
    }
    if (!createLink.trim()) {
      errors.link = intl.formatMessage({
        id: "AGENTS.HOME.REFERENCE_DATA.ERROR.LINK",
      });
    }
    if (!createDetail.trim()) {
      errors.detail = intl.formatMessage({
        id: "AGENTS.HOME.REFERENCE_DATA.ERROR.DETAIL",
      });
    }

    // If there are validation errors, display them and return
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (editingItem) {
      try {
        await updateLinkReferenceAsync({
          id: editingItem.id,
          data: { name: createName, link: createLink, detail: createDetail },
        });
        setLocalLinkReferenceData((prev) => ({
          ...prev,
          result: prev.result.map((it) =>
            it.id === editingItem.id
              ? {
                  ...it,
                  name: createName,
                  link: createLink,
                  detail: createDetail,
                }
              : it
          ),
        }));
        setCreateModalOpen(false);
        setEditingItem(null);
      } catch (error) {
        console.error("Lỗi khi cập nhật:", error);
      }
      return;
    }

    try {
      const response = await createLinkReferenceAsync({
        name: createName,
        link: createLink,
        detail: createDetail,
      });
      const newItem = {
        id: response?.id,
        name: createName,
        link: createLink,
        detail: createDetail,
        created_at: response?.created_at,
      };

      setLocalLinkReferenceData((prev) => {
        const newTotal = (prev.total || 0) + 1;
        const currentPageSize = prev.result.length;

        // Nếu page hiện tại đã đầy, chuyển về page cuối để hiển thị dữ liệu mới
        if (currentPageSize >= limit) {
          const lastPage = Math.ceil(newTotal / limit);

          setSearchParams((prevParams) => ({
            ...prevParams,
            page: lastPage,
          }));
          const invalidateQueries = setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: [
                "linkreference-data",
                searchParams?.page,
                searchParams?.limit,
                searchParams?.name,
              ],
            });
          }, 2000);
          return () => clearTimeout(invalidateQueries);
        }

        // Nếu page hiện tại còn chỗ, thêm dữ liệu mới vào đầu
        return {
          ...prev,
          result: [newItem, ...prev.result],
          total: newTotal,
        };
      });
      setCreateModalOpen(false);
    } catch (error) {
      console.error("Lỗi khi tạo mới:", error);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setCreateName(item.name || "");
    setCreateLink(item.link || "");
    setCreateDetail(item.detail || "");
    setCreateModalOpen(true);
  };

  const confirmDelete = async (item) => {
    try {
      await deleteLinkReferenceAsync(item.id);
      setLocalLinkReferenceData((prev) => ({
        ...prev,
        result: prev.result.filter((resultItem) => resultItem.id !== item.id),
        total: (prev.total || 1) - 1,
      }));
    } catch (error) {
      console.error("Lỗi khi xóa:", error);
    }
  };

  const columns = [
    {
      key: "name",
      dataIndex: "name",
      title: <FormattedMessage id="AGENTS.REFERENCE_DATA.NAME" />,
      type: "text",
      width: "300px",
      align: "left",
    },
    {
      key: "link",
      dataIndex: "link",
      title: <FormattedMessage id="AGENTS.LINK_REFERENCE_DATA.LINK" />,
      type: "text",
      width: "300px",
      align: "left",
    },
    {
      key: "detail",
      dataIndex: "detail",
      title: (
        <FormattedMessage id="AGENTS.LINK_REFERENCE_DATA.DETAIL_150_WORD" />
      ),
      type: "text",
      width: "300px",
      align: "left",
      render: (text, record) => {
        const isExpanded = expandedRows[record.id] || false;
        const needsExpansion = isMessageLong(text);
        // Xử lý ký tự xuống dòng \n thành <br /> tags
        const processedText = text ? text.replace(/\n/g, "  \n") : "";

        return (
          <div className="text-sm md:text-base w-full flex flex-col transition-all duration-300 ease-in-out">
            <div className="flex items-start">
              <div
                className={`${
                  !isExpanded && needsExpansion ? "line-clamp-1" : ""
                } flex-1 transition-all duration-300 ease-in-out overflow-hidden`}
              >
                <Markdown
                  components={markdownStyles}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                >
                  {processedText}
                </Markdown>
              </div>
              {needsExpansion && (
                <button
                  onClick={() =>
                    setExpandedRows((prev) => ({
                      ...prev,
                      [record.id]: !isExpanded,
                    }))
                  }
                  title={`${isExpanded ? "Thu nhỏ" : "Phóng to"}`}
                  className="ml-2 flex-shrink-0 hover:text-[#50b553]"
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronUp : faChevronDown}
                    className={`h-3 w-3 leading-none text-gray-500`}
                  />
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "created_at",
      dataIndex: "created_at",
      title: <FormattedMessage id="AGENTS.REFERENCE_DATA.DATE_CREATE" />,
      type: "text",
      width: "150px",
      align: "center",
      render: (text) => <p>{convertToVNTime(text)}</p>,
    },
    {
      key: "action",
      dataIndex: "action",
      title: <FormattedMessage id="AGENTS.REFERENCE_DATA.ACTION" />,
      type: "actions",
      width: "100px",
      align: "center",
      render: (_value, record) => (
        <div className="flex gap-4 justify-center ">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
            className="text-blue-500 hover:text-blue-300"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
          <Popconfirm
            title={<FormattedMessage id="AGENTS.LINK_REFERENCE_DATA.DELETE_TASK" />}
            description={
              <FormattedMessage id="AGENTS.LINK_REFERENCE_DATA.DELETE_CONFIRM" />
            }
            onConfirm={() => confirmDelete(record)}
            okText={<FormattedMessage id="AGENTS.REFERENCE_DATA.YES" />}
            cancelText={<FormattedMessage id="AGENTS.REFERENCE_DATA.NO" />}
          >
            <button className="text-red-500 hover:text-red-300">
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <button
        className={`px-3 py-2 w-full rounded-md bg-gray-100 hover:bg-gray-200 transition-colors text-sm ${className}`}
        onClick={() => setOpen(true)}
      >
        <FormattedMessage id="AGENTS.LINK_REFERENCE_DATA.MANAGE" />
      </button>
      {/* <Checkbox className="text-xs flex items-center"> */}
      {/*   Enable Auto Extract */}
      {/* </Checkbox> */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`${intl.formatMessage({ id: "AGENTS.LINK_REFERENCE_DATA.MANAGE_TITLE" })}`}
        width="90vw"
        maxWidth="5xl"
        className="max-h-[calc(100dvh-60px)] my-auto"
      >
        <div className="mt-4">
          <div className="flex justify-between flex-wrap gap-3 mb-4 items-center">
            <TableToolbar
              onSearchChange={handleSearchChange}
              onClickSearch={handleSearch}
              onOpenCreateModal={handleOpenCreateModal}
              onClickImport={handleImportClick}
              onFileChange={handleFileChange}
              fileRef={fileInputRef}
              tooltipLink={"https://media.chatme.vn/export_temp/temp_link.csv"}
              searchOptions={["name"]}
            />
            <div className="flex">
              <TablePagination
                current={page}
                pageSize={limit}
                total={localLinkReferenceData?.total || 0}
                onChange={handlePageChange}
                onShowSizeChange={handlePageSizeChange}
                pageSizeOptions={["5", "10", "20", "50", "80", "100"]}
              />
            </div>
          </div>
          <Table
            dataSource={localLinkReferenceData?.result}
            loading={linkreferenceLoading}
            columns={columns}
            rowKey="id"
            pagination={false}
            scroll={{ x: "max-content" }}
            bordered
            size="large"
            locale={{
              emptyText: (
                <FormattedMessage id="AGENTS.REFERENCE_DATA.NO_DATA" />
              ),
            }}
            className="custom-table"
            {...tableProps}
          />
          <div className="mt-4 flex justify-end">
            <TablePagination
              current={page}
              pageSize={limit}
              total={localLinkReferenceData?.total || 0}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
              pageSizeOptions={["5", "10", "20", "50", "80", "100"]}
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        title={
          editingItem ? (
            <FormattedMessage id="AGENTS.LINK_REFERENCE_DATA.UPDATE_LINK_REFERENCE_DATA" />
          ) : (
            <FormattedMessage id="AGENTS.LINK_REFERENCE_DATA.CREATE_NEW" />
          )
        }
        footer={null}
        width={editingItem ? "700px" : "500px"}
        centered
      >
        <div className="flex flex-col gap-4">
          <label htmlFor="name">
            <FormattedMessage id="AGENTS.REFERENCE_DATA.NAME" />:{" "}
          </label>
          <Tooltip
            title={validationErrors.name}
            open={!!validationErrors.name}
            placement="topLeft"
            color="#ff4d4f"
          >
            <input
              placeholder={intl.formatMessage({
                id: "AGENTS.HOME.REFERENCE_DATA_PLACEHOLDER.NAME",
              })}
              value={createName}
              onChange={(e) => {
                setCreateName(e.target.value);
                if (validationErrors.name) {
                  setValidationErrors((prev) => {
                    const next = { ...prev };
                    delete next.name;
                    return next;
                  });
                }
              }}
              autoFocus
              id="name"
              required
              className={`w-full border rounded px-3 py-2 bg-white text-sm hover:border-[#50B553] focus:border-[#50B553] outline-none ${
                validationErrors.name ? "border-red-500" : ""
              }`}
            />
          </Tooltip>
          <label htmlFor="data">
            <FormattedMessage id="AGENTS.LINK_REFERENCE_DATA.LINK" />:{" "}
          </label>
          <Tooltip
            title={validationErrors.link}
            open={!!validationErrors.link}
            placement="topLeft"
            color="#ff4d4f"
          >
            <input
              placeholder={intl.formatMessage({
                id: "AGENTS.HOME.REFERENCE_DATA_PLACEHOLDER.LINK",
              })}
              value={createLink}
              onChange={(e) => {
                setCreateLink(e.target.value);
                if (validationErrors.link) {
                  setValidationErrors((prev) => {
                    const next = { ...prev };
                    delete next.link;
                    return next;
                  });
                }
              }}
              rows={4}
              id="link"
              required
              className={`w-full h-30 border rounded px-3 py-2 bg-white text-sm hover:border-[#50B553] focus:border-[#50B553] outline-none ${
                validationErrors.link ? "border-red-500" : ""
              }`}
            />
          </Tooltip>
          <label htmlFor="data">
            <FormattedMessage id="AGENTS.LINK_REFERENCE_DATA.DETAIL" />:{" "}
          </label>
          <Tooltip
            title={validationErrors.detail}
            open={!!validationErrors.detail}
            placement="topLeft"
            color="#ff4d4f"
          >
            <textarea
              placeholder={intl.formatMessage({
                id: "AGENTS.LINK_REFERENCE_DATA.DETAIL_PLACEHOLDER",
              })}
              value={createDetail}
              onChange={(e) => {
                setCreateDetail(e.target.value);
                if (validationErrors.detail) {
                  setValidationErrors((prev) => {
                    const next = { ...prev };
                    delete next.detail;
                    return next;
                  });
                }
              }}
              rows={4}
              id="detail"
              required
              className={`w-full h-40 border rounded px-3 py-2 bg-white text-sm hover:border-[#50B553] focus:border-[#50B553] outline-none ${
                validationErrors.detail ? "border-red-500" : ""
              }`}
            />
          </Tooltip>
          <div className="flex justify-end gap-2 mt-2">
            <AntdButton onClick={handleCloseCreateModal}>
              <FormattedMessage id="AGENTS.REFERENCE_DATA.CANCEL" />
            </AntdButton>
            <AntdButton type="primary" onClick={handleSubmitCreate}>
              {editingItem ? (
                <FormattedMessage id="AGENTS.REFERENCE_DATA.UPDATE" />
              ) : (
                <FormattedMessage id="AGENTS.REFERENCE_DATA.SUBMIT" />
              )}
            </AntdButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

export { ManageLink };

ManageLink.propTypes = {
  tableProps: PropTypes.object,
  className: PropTypes.string,
};
